import { Injectable } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { SignalType } from 'angular-to-phaser';
import { AnnouncementComponent } from 'src/app/components/notifications/announcement/announcement.component';
import { PlayerStateUpdate, RandomEventNotification, Signal } from '../dto';
import { InventoryService } from './inventory.service';
import { AnnouncementNotification, SignalService } from './signal.service';
import { SystemNotificationService } from './system-notification.service';
import { UserService } from './user.service';

@Injectable({
    providedIn: 'root'
})
export class RandomEventService {
    private modalRef: NgbModalRef;

    constructor(
        private userService: UserService,
        private inventory: InventoryService,
        private notification: SignalService,
        private modalService: NgbModal,
        private systemNotificationService: SystemNotificationService) {
        this.notification.latestNotificationSignal.asObservable().subscribe(this.receiveNotification.bind(this));
    }

    private receiveNotification(value: Signal): void {
        if (value?.SignalType !== SignalType.RandomEvent) { return; }

        // A live push means the effect was *just* applied server-side - the client's already-
        // loaded cash/inventory predates it, so it needs patching in locally here and now.
        this.openAndAck(<RandomEventNotification>value, true);
    }

    // Shared by the live SignalR push and the Alerts mailbox tab (a notification the player
    // missed live, fetched later) - opening the popup, either way, immediately acks the
    // underlying system-notification queue row.
    //
    // applyLocally must be false for anything fetched from the queue: that notification's effect
    // already happened server-side in the past, and SystemNotificationService already resynced
    // cash/inventory from the server as soon as it found a pending notification (see
    // SystemNotificationService.fetchAndAdvance) - patching the delta in again here would double
    // it in the UI (a "mirage" that only clears on a manual refresh, since the DB was only ever
    // touched once).
    public openAndAck(notification: RandomEventNotification, applyLocally: boolean): void {
        const ann = new AnnouncementNotification(notification.AlertText, notification.Title ?? 'Something happened!', notification);

        if (!this.modalRef || !this.modalRef.componentInstance) {
            this.modalRef = this.modalService.open(AnnouncementComponent);
            (<AnnouncementComponent>this.modalRef.componentInstance).initialize(ann);
        }

        this.notification.addAnnouncement(ann);

        if (applyLocally) {
            PlayerStateUpdate.distributeToServices(notification.PlayerStateUpdate, this.inventory, this.userService);
        }

        if (notification.Id) {
            this.systemNotificationService.ack(notification.Id);
        }
    }
}
